import { useTranslation } from 'react-i18next';
import { Alert } from '@/common/components/Alert/Alert';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { Card } from '@/common/components/Card/Card';
import { Divider } from '@/common/components/Divider/Divider';
import { Heading } from '@/common/components/Heading/Heading';
import { Label } from '@/common/components/Label/Label';
import { LabeledValue } from '@/common/components/LabeledValue/LabeledValue';
import { Stack } from '@/common/components/Stack/Stack';
import { Translate } from '@/common/components/Translate/Translate';
import { MARKET } from '@/common/constants/markets';
import {
  TInsurancePlanSimulation,
  TMemberSimulation,
  TMenhirInsurancePaymentMethod,
} from '@/common/integrations/menhir/employee/insurance/insurance.types';
import { useMarket } from '@/web/hooks/useMarket';
import {
  MoneyUtils,
  defaultMoney,
  moneyToString,
} from '@coverflex-tech/hypernova-utils';

export const CostSummaryTable = ({
  simulation,
  paymentMethod,
  selectedPersons,
}: TCostSummaryTableProps) => {
  const { t } = useTranslation();
  const { slug } = useMarket();

  const totalPaidByCompany = selectedPersons.reduce(
    (acc, person) => ({
      amount: acc.amount + person.paidByCompany.amount,
      currency: person.paidByCompany.currency,
    }),
    defaultMoney
  );

  const totalCost = selectedPersons.reduce(
    (acc, person) => ({
      amount:
        acc.amount + person.paidByEmployee.amount + person.paidByCompany.amount,
      currency: person.paidByEmployee.currency,
    }),
    defaultMoney
  );

  const hasCivilPartner = selectedPersons.some(
    (person) => person.type === 'civil-partner'
  );

  return (
    <Card plain>
      <Stack gap="section">
        <Heading level={4}>{t('general.labels.monthly_costs')}</Heading>
        <Stack gap="md">
          <Stack gap="sm">
            {selectedPersons.map((person) => (
              <LabeledValue
                key={person.id}
                orientation="horizontal"
                label={person.shortName}
                value={moneyToString(
                  MoneyUtils.sum([person.paidByEmployee, person.paidByCompany])
                )}
              />
            ))}
          </Stack>
          <Divider />
          <Stack gap="sm">
            {simulation.totalCost.amount > 0 && (
              <LabeledValue
                orientation="horizontal"
                label={t('general.labels.total_cost')}
                value={moneyToString(totalCost)}
              />
            )}
            {totalPaidByCompany.amount > 0 && (
              <LabeledValue
                orientation="horizontal"
                label={t('general.labels.paid_by_company')}
                value={`-${moneyToString(totalPaidByCompany)}`}
              />
            )}
          </Stack>
          <Divider />
          {totalCost.amount > 0 && (
            <LabeledValue
              orientation="horizontal"
              label={
                <Label bold size="md">
                  {t('general.labels.paid_by_you')}
                </Label>
              }
              value={
                <Label bold size="lg">
                  {moneyToString(simulation?.totalCost)}
                </Label>
              }
            />
          )}
        </Stack>
        {slug !== MARKET.ES &&
          paymentMethod === 'wallet' &&
          simulation.savingsAmount &&
          simulation.savingsAmount.amount > 0 && (
            <Alert type="info" noBackground>
              <Translate
                i18nKey={
                  'dashboard.insurance_detail.add_family_members.confirm.monthly_costs.estimated_savings.alert'
                }
                values={{
                  amount: moneyToString(simulation.savingsAmount),
                }}
                components={{ strong: <BodyText bold color="systemInfo" /> }}
              />
            </Alert>
          )}
        {slug === MARKET.ES &&
          simulation.metadata?.exemptionAmount &&
          simulation.metadata.exemptionAmount.amount > 0 && (
            <Alert type="info" noBackground>
              <Translate
                i18nKey={
                  'dashboard.insurance_detail.add_family_members.confirm.monthly_costs.exemption_amount.alert'
                }
                values={{
                  amount: moneyToString(simulation.metadata.exemptionAmount),
                }}
                components={{ strong: <BodyText bold color="systemInfo" /> }}
              />
            </Alert>
          )}
        {slug === MARKET.ES &&
          simulation.metadata?.exemptionAmount &&
          simulation.metadata.exemptionAmount.amount > 0 &&
          hasCivilPartner && (
            <Alert type="info" noBackground>
              <Translate
                i18nKey={
                  'dashboard.insurance_detail.add_family_members.confirm.monthly_costs.civil_partner.alert'
                }
                components={{ strong: <BodyText bold color="systemInfo" /> }}
              />
            </Alert>
          )}
        <BodyText size="sm" color="neutral20">
          {t(
            'dashboard.insurance_detail.add_family_members.confirm.monthly_costs.description'
          )}
        </BodyText>
      </Stack>
    </Card>
  );
};

export type TCostSummaryTableProps = {
  simulation: TInsurancePlanSimulation;
  paymentMethod?: TMenhirInsurancePaymentMethod;
  selectedPersons: TMemberSimulation[];
};
